import { $isMarkNode, MarkNode } from '@lexical/mark';
import { $getRoot, $isElementNode, ElementNode } from 'lexical';
import { cloneDeep } from 'lodash';
import { $removeNodeAbbreviation } from 'modules/Lexical/Plugins/AbbreviationPlugin/utils';
import * as Models from 'models';
import { isCallToAction, isTextComponent } from 'utils/entityType';
import { createLexicalCTAEditor, createLexicalTextEditor } from '../editors';
import { removeAbbreviationsIfNotPresentDraftJS } from './_LEGACY_draftjs/helpers';

export function $getAbbreviations(node: ElementNode): MarkNode[] {
  const markNodes = $isMarkNode(node) ? [node] : [];

  if ($isElementNode(node)) {
    const children = node.getChildren();
    children.forEach(child => markNodes.push(...$getAbbreviations(child as ElementNode)));
  }

  return markNodes;
}

function $removeMissingAbbreviations(textAbbreviationDocuments: Models.TextAbbreviationDocumentsArray): void {
  const root = $getRoot();
  const markNodes = $getAbbreviations(root);

  markNodes.forEach((markNode) => {
    const [abbreviationId] = markNode.getIDs();
    const isTextAbbreviationDocumentPresent = Boolean(textAbbreviationDocuments[abbreviationId]);
    if (!isTextAbbreviationDocumentPresent) {
      $removeNodeAbbreviation(markNode);
    }
  });
}

/**
 * Main function that removes abbreviations from the lexical state if they are not present
 * in the textAbbreviationDocuments. It converts the lexical state from a JSON string before processing.
 *
 * @param textAbbreviationDocuments - A collection of documents for validation.
 * @param lexicalState - A JSON string representing the lexical state.
 * @returns A JSON string representing the updated lexical state.
 */
export function removeTextAbbreviationsIfNotPresent(
  textAbbreviationDocuments: Models.TextAbbreviationDocumentsArray,
  lexicalState: string,
): string {
  const editor = createLexicalTextEditor(lexicalState);
  editor.update(() => {
    $removeMissingAbbreviations(textAbbreviationDocuments);
  }, { discrete: true });

  return JSON.stringify(editor.getEditorState());
}

export function removeAbbreviationsOnComponentIfNotPresent<T extends Models.TextComponent | Models.CallToAction>(
  component: T,
  textAbbreviationDocuments: Models.TextAbbreviationDocumentsArray,
): T {
  const { lexicalState } = component;
  if (!lexicalState) {
    const updatedComponent = cloneDeep(component);
    updatedComponent.rawContent = removeAbbreviationsIfNotPresentDraftJS(
      textAbbreviationDocuments,
      updatedComponent.rawContent,
    );

    return updatedComponent;
  }
  let updatedLexicalState: string;
  const isCTA = isCallToAction(component);
  if (isCTA) {
    const editor = createLexicalCTAEditor(lexicalState);
    editor.update(() => {
      $removeMissingAbbreviations(textAbbreviationDocuments);
    }, { discrete: true });
    updatedLexicalState = JSON.stringify(editor.getEditorState());
  } else {
    updatedLexicalState = removeTextAbbreviationsIfNotPresent(
      textAbbreviationDocuments,
      lexicalState,
    );
  }

  if (lexicalState === updatedLexicalState) {
    return component;
  }

  const updatedComponent = cloneDeep(component);
  updatedComponent.lexicalState = updatedLexicalState;

  return updatedComponent;
}

export function hasTextAbbreviations(lexicalState: string): boolean {
  const editor = createLexicalTextEditor(lexicalState);

  return editor.read(() => {
    const root = $getRoot();
    const markNodes = $getAbbreviations(root);

    return Boolean(markNodes.length);
  });
}

export function getAbbreviationIdsFromDocument(document: Models.TextComponentMap | Models.CallToActionMap | undefined): string[] {
  const ids: string[] = [];
  if (!document) {
    return ids;
  }
  const lexicalState = (document as Models.TextComponentMap).get('lexicalState');
  if (!lexicalState) {
    return ids;
  }
  const editor = isTextComponent(document) ? createLexicalTextEditor(lexicalState) : createLexicalCTAEditor(lexicalState);

  editor.read(() => {
    const abbrs = $getAbbreviations($getRoot());
    abbrs.forEach((node) => {
      const [abbreviationId] = node.getIDs();
      ids.push(abbreviationId);
    });
  });

  return ids;
}
